import classnames from 'classnames';
import * as React from 'react';

export interface ITableColumnModel {
    accessor: any;
    Header?: any;
    className?: string;
    disableRowHandler?: boolean;
}

export interface IProps {
    children?: React.ReactNode;
    data: any[];
    columns: ITableColumnModel[];
    actionButtons?: any;
    filters?: any;
    multiselectDataProp?: string;
    onClickRow?: (onClickRowReturnProp: any) => void;
    onClickRowReturnData?: string[] | string;
    selectedRow?: string;
    selectedRowDataProp?: string;
}

interface IState {
    selection: any[];
    selectAll: boolean;
}

export default class ControlledTable extends React.Component<IProps, IState> {
    public state: IState = {
        selection: [],
        selectAll: false,
    };

    private getAllKeys = (): any[] => {
        const { data, multiselectDataProp } = this.props;
        return multiselectDataProp ? data.map((item: any) => item[multiselectDataProp]) : [];
    };

    private toggleAll = (): void => {
        const selectAll = !this.state.selectAll;
        const selection = selectAll ? this.getAllKeys() : [];

        this.setState((prevState: IState) => ({
            ...prevState,
            selectAll,
            selection,
        }));
    };

    private isSelected = (key: any): boolean => {
        return this.state.selection.includes(key);
    };

    private setSelection = (selection: any[]): void => {
        this.setState((prevState: IState) => ({
            ...prevState,
            selection,
        }));
    };

    private addToSelection = (key: any): void => {
        const selection = [...this.state.selection, key];

        this.setSelection(selection);
    };

    private removeFromSelection = (key: any): void => {
        const selection = this.state.selection.filter((item: any) => item !== key);

        this.setSelection(selection);
    };

    private toggleSelect = (key: any) => () => {
        this.isSelected(key) ? this.removeFromSelection(key) : this.addToSelection(key);
    };

    private renderMultiSelectCell = (value: any) => {
        const checked = this.isSelected(value);

        return (
            <td onClick={this.toggleSelect(value)} className="is-checkbox-cell">
                <input type="checkbox" checked={checked} readOnly={true} />
            </td>
        );
    };

    private renderTableActions = () => {
        const mulitSelectRow = this.props.multiselectDataProp ? 1 : 0;
        const colSpan = this.props.columns.length + mulitSelectRow;
        return (
            <tr className="table-actions-row">
                <td colSpan={colSpan}>
                    {this.props.actionButtons(this.state.selection, this.toggleAll)}
                </td>
            </tr>
        );
    };

    private renderTableFilters = () => {
        const mulitSelectRow = this.props.multiselectDataProp ? 1 : 0;
        const colSpan = this.props.columns.length + mulitSelectRow;

        return (
            <tr className="table-filters-row">
                <td colSpan={colSpan}>
                    {this.props.filters(this.state.selection, this.toggleAll)}
                </td>
            </tr>
        );
    };

    private getCellDisplayValue = (row: any, column: ITableColumnModel) => {
        const { accessor } = column;
        switch (typeof accessor) {
            case 'string':
                return row[accessor];
            case 'function':
                return accessor(row);
            default:
                return;
        }
    };

    private renderTableHeadItem = (header: ITableColumnModel, index: number) => {
        return (
            <th key={index} className={header.className}>
                {header.Header}
            </th>
        );
    };

    private renderTableHead = () => {
        const { columns, multiselectDataProp } = this.props;
        const mulitSelectRow = multiselectDataProp ? <th /> : null;

        return (
            <thead>
                <tr>
                    {mulitSelectRow}
                    {columns.map((header, i: number) => this.renderTableHeadItem(header, i))}
                </tr>
            </thead>
        );
    };

    private renderTableCell = (row: any, column: ITableColumnModel, index: number) => {
        return (
            <td
                key={index}
                className={column.className}
                onClick={!column.disableRowHandler ? this.onClickRowHandler(row) : undefined}
            >
                {typeof this.getCellDisplayValue(row, column) === 'boolean' &&
                    String(this.getCellDisplayValue(row, column))}
                {this.getCellDisplayValue(row, column)}
            </td>
        );
    };

    private onClickRowHandler = (row: any) => () => {
        const { onClickRow, onClickRowReturnData } = this.props;
        if (!onClickRow || !onClickRowReturnData) {
            return;
        }
        const returnData = Array.isArray(onClickRowReturnData)
            ? onClickRowReturnData.map(selector => row[selector])
            : row[onClickRowReturnData];

        onClickRow(returnData);
    };

    private getRowClass = (dataRow: any) => {
        const { onClickRow, selectedRow, selectedRowDataProp } = this.props;

        let ret: string = onClickRow ? 'is-clickable' : '';

        if (selectedRow && selectedRowDataProp && dataRow[selectedRowDataProp] === selectedRow) {
            ret += ' is-active';
        }

        return ret;
    };

    private renderTableBody = () => {
        const { columns, data, actionButtons, multiselectDataProp, filters } = this.props;

        return (
            <tbody>
                {filters && this.renderTableFilters()}
                {actionButtons && this.renderTableActions()}
                {data.map((row: any, key: number) => (
                    <tr key={key} className={this.getRowClass(row)}>
                        {multiselectDataProp &&
                            this.renderMultiSelectCell(row[multiselectDataProp])}
                        {columns.map((column: ITableColumnModel, i: number) =>
                            this.renderTableCell(row, column, i),
                        )}
                    </tr>
                ))}
            </tbody>
        );
    };

    public render() {
        const { multiselectDataProp, actionButtons, filters } = this.props;

        const classes = classnames([
            { 'is-multiselect': multiselectDataProp },
            { 'has-actions': actionButtons },
            { 'has-filters': filters },
        ]);

        return (
            <>
                <table className={classnames('table is-fullwidth', classes)}>
                    {this.renderTableHead()}
                    {this.renderTableBody()}
                </table>
            </>
        );
    }
}
